import React from "react";
import { useTranslation } from "react-i18next";
import { isResolvedBadge } from "./handler/badge.helper";
import { BotCheckState, FlightBadgeState, FlightDirection } from "./types";

export interface AlertsCtaProps {
  badge: FlightBadgeState;
  botCheckState: BotCheckState;
  direction: FlightDirection;
  /** The flight's date is before today (HKT) — it has already flown, so there's nothing to watch. */
  isPastDate?: boolean;
  onStart: () => void;
}

export const AlertsCta: React.FC<AlertsCtaProps> = ({
  badge,
  botCheckState,
  direction,
  isPastDate = false,
  onStart,
}) => {
  const { t } = useTranslation("flightAlerts");

  if (isResolvedBadge(badge) || isPastDate) {
    const resolvedNoteKey = !isResolvedBadge(badge)
      ? "results.resolvedNotePast"
      : badge === "cancelled"
        ? "results.resolvedNoteCancelled"
        : direction === "arrival"
          ? "results.resolvedNoteArrival"
          : "results.resolvedNoteDeparture";

    return (
      <div className="rounded-2xl border-2 border-ink-12 bg-card p-8">
        <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
          {t(resolvedNoteKey)}
        </p>
      </div>
    );
  }

  const isDisabled = botCheckState === "unavailable";

  return (
    <div className="flex flex-col gap-5 rounded-2xl border-2 border-ink-12 bg-card p-8">
      {/* Figma spec: Lexend Deca, Bold 700, 20px/24px, -0.4px
          letter-spacing, ink/100 (DASH-6077). Sized down on mobile to match
          the button's mobile downsizing. */}
      <h3 className="font-lexend text-[18px]/[22px] font-bold tracking-[-0.4px] text-ink-100 md:text-[20px]/[24px]">
        {t("results.ctaHeading")}
      </h3>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => {
          if (!isDisabled) onStart();
        }}
        className={`flex h-14 w-full items-center justify-center rounded-2xl font-lexend text-[14px]/[18px] font-bold tracking-normal text-white md:text-[16px]/[20px] ${
          isDisabled ? "cursor-not-allowed bg-gray-300" : "bg-primary-100"
        }`}
      >
        {t("results.ctaButton")}
      </button>
      {isDisabled && (
        <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
          {t("results.ctaDisabledNote")}
        </p>
      )}
    </div>
  );
};
